<script setup lang="ts">
import BaseLayout from "@/layouts/BaseLayout.vue";
import {router, useForm} from "@inertiajs/vue3";
import {route} from "ziggy-js";
import VFieldLabel from "@/components/forms/VFieldLabel.vue";
import {useAuth} from "@/composables/auth";
import VEditor from "@/components/forms/VEditor.vue";
import {ref, watch} from "vue";
import {Assessment, CapacityFramework, UserSummary} from "@/types";
import axios from "axios";
import {useDate} from "@/composables/date";
import VersionChip from "@/components/chips/VersionChip.vue";

defineOptions({layout: BaseLayout})

const { user } = useAuth()
const {shortDate} = useDate()

interface Props {
    title: string
    capacityFramework: CapacityFramework
    assessment: Assessment
}

const props = defineProps<Props>();

const form = useForm({
    title: props.assessment.title,
    description: props.assessment.description,
    user: props.assessment.user,
    is_public: props.assessment.is_public,
    start_date: props.assessment.start_date,
    due_date: props.assessment.due_date,
})

const isBeingEdited = ref<boolean>(false)
const isDateBeingEdited = ref<boolean>(false)
const search = ref<string>('')
const items = ref<Array<UserSummary>>([])

const submit = () => {
    form.put(route('assessments.update', { assessment: props.assessment.id }))
}

const goBack = () => {
    router.get(route('assessments.index'))
}

const toggleEdit = (value?: UserSummary | null) => {
    if (!value) return
    isBeingEdited.value = !isBeingEdited.value;
    if (isBeingEdited.value) {
        search.value = form.user.name || '';
    } else {
        search.value = '';
    }
}

const toggleDateEdit = () => {
    isDateBeingEdited.value = !isDateBeingEdited.value;
}

const closeEdit = () => {
    isBeingEdited.value = false;
    search.value = '';
}

const closeDateEdit = () => {
    isDateBeingEdited.value = false;
}

const loadItems = async (search: string) => {
    try {
        const { data } = await axios.get(route('teams.members', { team: props.assessment.team_id }), {
            params: { search }
        });

        items.value = data;
    } catch (error) {
        items.value = [];
    }
};

const assignYourself = () => {
    form.user = user.value;
    isBeingEdited.value = false;
}

watch(search, (newSearch) => {
    loadItems(newSearch);
}, {immediate: true})
</script>

<template>
    <v-container>
        <v-row class="align-center justify-space-between px-3">
            <h1 class="text-h4 font-weight-bold">
                {{ props.title }}
            </h1>
        </v-row>
        <v-form
            @submit.prevent="submit"
        >
            <v-row
                class="mt-5"
            >
                <v-col
                    cols="12"
                    class="py-0"
                >
                    <v-field-label>
                        Title (required)
                    </v-field-label>
                    <v-text-field
                        v-model="form.title"
                        type="text"
                        name="title"
                        class="mb-3"
                        density="compact"
                        :hide-details="!form.errors.title"
                        :error-messages="form.errors.title"
                        autofocus
                    />
                </v-col>
                <v-col
                    cols="12"
                    md="8"
                    class="py-0"
                >
                    <v-field-label>
                        Description
                    </v-field-label>
                    <v-editor
                        v-model="form.description"
                        name="description"
                        placeholder="Write a comment"
                    />
                    <v-checkbox
                        v-model="form.is_public"
                        class="mt-3 text-body-2"
                        density="compact"
                        :true-value="false"
                        :false-value="true"
                        color="primary"
                        hide-details
                    >
                        <template #label>
                            <span class="text-body-2">
                                Turn on confidentiality: Limit visibility of answers to resource members only
                            </span>
                        </template>
                    </v-checkbox>
                </v-col>
                <v-col
                    cols="12"
                    md="4"
                >
                    <v-list class="pa-0">
                        <v-list-item>
                            <v-list-item-title class="text-body-2 text-black font-weight-bold">
                                Framework
                                <version-chip>
                                    {{ props.capacityFramework.version_id }}
                                </version-chip>
                            </v-list-item-title>
                            <v-list-item-subtitle>
                                {{ props.capacityFramework.name }}
                            </v-list-item-subtitle>

                            <v-divider class="mt-3 mb-1" />
                        </v-list-item>
                        <v-list-item v-click-outside="{ handler: closeEdit }">
                            <v-list-item-title class="text-body-2 text-black font-weight-bold">
                                Main contact
                                <v-btn
                                    color="black"
                                    variant="text"
                                    size="x-small"
                                    class="float-end py-1 px-2"
                                    @click="toggleEdit"
                                >
                                    <span class="font-weight-regular">
                                        {{ isBeingEdited ? 'Apply' : 'Edit' }}
                                    </span>
                                </v-btn>
                            </v-list-item-title>
                            <v-list-item-subtitle
                                v-if="!isBeingEdited"
                                class="d-inline-flex"
                            >
                                {{ form.user?.name || 'None' }} <div
                                    v-if="!form.user"
                                    class="ml-1"
                                >
                                    - <span
                                        class="custom-link"
                                        @click="assignYourself"
                                    >assign yourself</span>
                                </div>
                            </v-list-item-subtitle>
                            <v-autocomplete
                                v-if="isBeingEdited"
                                v-model="form.user"
                                v-model:search="search"
                                variant="outlined"
                                :items="items"
                                density="compact"
                                return-object
                                hide-details
                                no-filter
                                item-title="name"
                                item-value="id"
                                hide-selected
                                no-data-text="No users found"
                                :list-props="{
                                    class: 'py-0'
                                }"
                                :menu-props="{
                                    location: 'bottom'
                                }"
                                menu-icon="null"
                                @update:model-value="toggleEdit($event)"
                            >
                                <template #item="{ item, props: binds }">
                                    <v-list-item
                                        :value="binds.value"
                                        link
                                        @click="binds.onClick as Event"
                                    >
                                        <v-list-item-title
                                            class="text-body-2 font-weight-bold"
                                        >
                                            {{ item.raw.name }}
                                        </v-list-item-title>
                                        <v-list-item-subtitle
                                            class="text-body-2 text-medium-emphasis"
                                        >
                                            {{ item.raw.email }}
                                        </v-list-item-subtitle>
                                    </v-list-item>
                                </template>
                            </v-autocomplete>
                            <small
                                v-if="form.errors.user"
                                class="d-block text-error"
                            >
                                {{ form.errors.user }}
                            </small>
                            <v-divider class="mt-3 mb-1" />
                        </v-list-item>
                        <v-list-item>
                            <v-list-item-title class="text-body-2 text-black font-weight-bold mb-2">
                                Resource
                            </v-list-item-title>
                            <v-list-item-subtitle>
                                {{ props.assessment.team.name }}
                            </v-list-item-subtitle>

                            <v-divider class="mt-3 mb-1" />
                        </v-list-item>
                        <v-list-item v-click-outside="{ handler: closeDateEdit }">
                            <v-list-item-title class="text-body-2 text-black font-weight-bold">
                                Dates
                                <v-btn
                                    color="black"
                                    variant="text"
                                    size="x-small"
                                    class="float-end py-1 px-2"
                                    @click="toggleDateEdit"
                                >
                                    <span class="font-weight-regular">
                                        {{ isDateBeingEdited ? 'Apply' : 'Edit' }}
                                    </span>
                                </v-btn>
                            </v-list-item-title>
                            <div
                                v-if="!isDateBeingEdited"
                                class="mb-2 text-body-2"
                            >
                                Start: <span class="ml-1 text-medium-emphasis">{{ form.start_date ? shortDate(form.start_date) : 'None' }}</span>
                            </div>
                            <v-list-item-subtitle
                                v-if="isDateBeingEdited"
                            >
                                <v-text-field
                                    v-model="form.start_date"
                                    type="date"
                                    density="compact"
                                    max-width="80%"
                                    hide-details
                                    clearable
                                >
                                    <template #prepend>
                                        <span class="text-body-2">Start:</span>
                                    </template>
                                </v-text-field>
                            </v-list-item-subtitle>
                            <div
                                v-if="!isDateBeingEdited"
                                class="mb-2 text-body-2"
                            >
                                Due: <span class="ml-3 text-medium-emphasis">{{ form.due_date ? shortDate(form.due_date) : 'None' }}</span>
                            </div>
                            <v-list-item-subtitle
                                v-if="isDateBeingEdited"
                            >
                                <v-text-field
                                    v-model="form.due_date"
                                    type="date"
                                    density="compact"
                                    max-width="80%"
                                    hide-details
                                >
                                    <template #prepend>
                                        <span class="text-body-2">Due:</span>
                                    </template>
                                </v-text-field>
                            </v-list-item-subtitle>
                        </v-list-item>
                    </v-list>
                </v-col>
            </v-row>
            <v-row>
                <v-col
                    cols="12"
                >
                    <v-btn
                        color="primary"
                        type="submit"
                    >
                        Save changes
                    </v-btn>
                </v-col>
            </v-row>
        </v-form>
    </v-container>
</template>

<style scoped>
.text-sm {
    font-size: 0.825rem !important;
}
</style>
